import React, { useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { publicApi } from '../../lib/api'
import { useAuth } from '../../context/AuthProvider'
import { usePageMeta } from '../../hooks/usePageMeta'
import EventCard from '../../components/events/EventCard'
import { Button } from '../../components/ui/button'
import FundUseDisclosure from '../../components/giving/FundUseDisclosure'
import FeeDisclosure from '../../components/giving/FeeDisclosure'
import { Input } from '../../components/ui/input'
import {
  MapPin,
  Phone,
  Mail,
  Clock,
  ExternalLink,
  Users,
  Calendar,
  Loader2,
} from 'lucide-react'

const PRESET_AMOUNTS = [10, 25, 50, 100]

// Fully self-contained public parish page: intentionally does not share
// markup/components with the authenticated donor parish page
// (pages/app/donor/ParishDetail.jsx). The two pages serve different
// audiences and have historically diverged in layout/spacing needs — keeping
// them separate avoids one page's styling fix silently affecting the other.
export default function ParishPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { session } = useAuth()

  const [parish, setParish] = useState(null)
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [error, setError] = useState(null)
  const [amount, setAmount] = useState('')

  usePageMeta({
    title: parish ? `${parish.name} — Claritas` : 'Parish — Claritas',
    description: parish?.briefBio || parish?.description || 'Support your parish through secure giving.',
  })

  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      setNotFound(false)
      setError(null)
      try {
        const data = await publicApi.getParish(id)
        if (cancelled) return
        setParish(data)
        const eventsResponse = await publicApi.getParishEvents(id, { limit: 5 })
        if (!cancelled) setEvents(eventsResponse.data || [])
      } catch (err) {
        if (cancelled) return
        if (err.status === 404) setNotFound(true)
        else setError(err.message)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [id])

  const handleDonate = () => {
    const dollars = parseFloat(amount)
    if (isNaN(dollars) || dollars < 0.5) {
      setError('Please enter an amount of at least $0.50')
      return
    }
    navigate(`/donate/${id}?amount=${dollars}`)
  }

  const formatAddress = () => {
    if (!parish) return null
    const parts = [parish.address, parish.city, parish.state, parish.zipCode].filter(Boolean)
    return parts.length > 0 ? parts.join(', ') : null
  }

  const formatMassTimes = () => {
    if (!parish?.parishInfo?.massTimes) return null

    const massTimes = parish.parishInfo.massTimes

    if (Array.isArray(massTimes)) {
      if (massTimes.length === 0) return null
      if (typeof massTimes[0] === 'object' && massTimes[0].day && Array.isArray(massTimes[0].times)) {
        return massTimes.map((dayGroup, index) => (
          <div key={index} className="py-2">
            <div className="font-semibold text-charcoal mb-1">{dayGroup.day}</div>
            <div className="space-y-0.5 pl-3">
              {dayGroup.times.map((slot, si) => (
                <div key={si} className="text-base text-charcoal/90 flex items-center gap-2">
                  <span>{slot.time}</span>
                  {slot.note && <span className="text-sm text-charcoal-muted">({slot.note})</span>}
                </div>
              ))}
            </div>
          </div>
        ))
      }
      if (typeof massTimes[0] === 'object' && massTimes[0].day) {
        const grouped = {}
        massTimes.forEach(entry => {
          if (!grouped[entry.day]) grouped[entry.day] = []
          grouped[entry.day].push(entry.time)
        })
        return Object.entries(grouped).map(([day, times]) => (
          <div key={day} className="py-2">
            <div className="font-semibold text-charcoal mb-1">{day}</div>
            <div className="space-y-0.5 pl-3">
              {times.map((time, i) => (
                <div key={i} className="text-base text-charcoal/90">{time}</div>
              ))}
            </div>
          </div>
        ))
      }
      return massTimes.map((time, index) => (
        <div key={index} className="text-base text-charcoal/90 py-1">
          {typeof time === 'string' ? time : JSON.stringify(time)}
        </div>
      ))
    }

    if (typeof massTimes === 'object') {
      return Object.entries(massTimes).map(([day, time]) => (
        <div key={day} className="text-base text-charcoal/90 py-1 flex justify-between items-center">
          <span className="font-semibold">{day}:</span>
          <span>{time}</span>
        </div>
      ))
    }

    if (typeof massTimes === 'string') {
      return (
        <div className="text-base text-charcoal/90 py-1">
          {massTimes}
        </div>
      )
    }

    return null
  }

  const getGoogleMapsUrl = () => {
    if (!parish) return null
    const address = formatAddress()
    if (!address) return null
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`
  }

  const formatConfessionTimes = () => {
    const times = parish?.parishInfo?.confessionTimes
    if (!times || !Array.isArray(times) || times.length === 0) return null

    if (typeof times[0] === 'object' && times[0].day && Array.isArray(times[0].times)) {
      return times.map((dayGroup, index) => (
        <div key={index} className="py-2">
          <div className="font-semibold text-charcoal mb-1">{dayGroup.day}</div>
          <div className="space-y-0.5 pl-3">
            {dayGroup.times.map((slot, si) => (
              <div key={si} className="text-base text-charcoal/90 flex items-center gap-2">
                <span>{slot.startTime} &ndash; {slot.endTime}</span>
                {slot.note && <span className="text-sm text-charcoal-muted">({slot.note})</span>}
              </div>
            ))}
          </div>
        </div>
      ))
    }

    return times.map((entry, index) => (
      <div key={index} className="text-base text-charcoal/90 py-1 flex justify-between items-center">
        <span className="font-semibold">{entry.day}:</span>
        <span>{entry.time}</span>
      </div>
    ))
  }

  const formatParishStaff = () => {
    const staff = parish?.parishInfo?.parishStaff
    if (!staff || !Array.isArray(staff) || staff.length === 0) return null
    return staff
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-8 h-8 text-gold animate-spin" />
      </div>
    )
  }

  if (notFound) {
    return (
      <div className="max-w-2xl mx-auto py-24 text-center">
        <h1 className="text-2xl font-bold font-display text-charcoal mb-2">
          We couldn't find that parish
        </h1>
        <p className="text-charcoal-muted mb-6">
          It may have been removed, or the link may be incorrect.
        </p>
        <Link to="/parishes" className="text-gold font-semibold">
          Browse all parishes &rarr;
        </Link>
      </div>
    )
  }

  if (error && !parish) {
    return (
      <div className="max-w-2xl mx-auto py-24 text-center">
        <h1 className="text-2xl font-bold font-display text-charcoal mb-2">
          Something went wrong
        </h1>
        <p className="text-charcoal-muted mb-6">
          {error || "We couldn't load this parish. Please try again."}
        </p>
        <Link to="/parishes" className="text-gold font-semibold">
          Browse all parishes &rarr;
        </Link>
      </div>
    )
  }

  const massTimesContent = formatMassTimes()
  const hasMassTimes = massTimesContent !== null
  const confessionTimesContent = formatConfessionTimes()
  const hasConfessionTimes = confessionTimesContent !== null
  const staffData = formatParishStaff()
  const hasStaff = staffData !== null
  const address = formatAddress()
  const googleMapsUrl = getGoogleMapsUrl()

  return (
    <div className="max-w-4xl mx-auto px-4 py-10 space-y-6">
      {parish.imageUrl && (
        <div className="relative w-full h-64 md:h-96 rounded overflow-hidden">
          <img
            src={parish.imageUrl}
            alt={parish.name}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.style.display = 'none'
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-background-dark/50 to-transparent" />
        </div>
      )}

      <div className="bg-white border border-surface-border rounded p-6">
        <h2 className="text-2xl font-bold font-display text-charcoal mb-3">{parish.name}</h2>
        {parish.briefBio && (
          <p className="text-lg text-charcoal/90 leading-relaxed mb-4">
            {parish.briefBio}
          </p>
        )}
        {parish.description && (
          <div className="mt-4 pt-4 border-t border-surface-border">
            <p className="text-base text-charcoal-muted leading-relaxed whitespace-pre-wrap">
              {parish.description}
            </p>
          </div>
        )}
      </div>

      <div className="bg-white border border-surface-border rounded shadow-card p-6">
        <h2 className="text-lg font-bold font-display text-charcoal mb-4">
          Make a one-time donation
        </h2>

        {parish.canAcceptDonations && parish.giving?.mode !== 'unavailable' ? (
          <>
            <div className="flex flex-wrap gap-2 mb-4">
              {PRESET_AMOUNTS.map((preset) => (
                <Button
                  key={preset}
                  variant={String(preset) === amount ? 'default' : 'outline'}
                  onClick={() => setAmount(String(preset))}
                >
                  ${preset}
                </Button>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                type="number"
                min="0.50"
                step="0.01"
                placeholder="Other amount"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                aria-label="Donation amount in dollars"
              />
              <Button onClick={handleDonate} className="bg-gold text-black font-semibold">
                Donate
              </Button>
            </div>
            <FundUseDisclosure parishName={parish.name} fundUse={parish.giving?.fundUse} />
            <FeeDisclosure
              percent={parish.giving?.fee?.oneTimePercent}
              fixedCents={parish.giving?.fee?.fixedCents}
              parishName={parish.name}
            />
            {error && <p className="text-red-600 text-sm mt-3">{error}</p>}
            {!session && (
              <p className="text-sm text-charcoal-muted mt-4">
                <Link to="/sign-in" className="text-gold font-semibold">
                  Sign in to set up recurring giving
                </Link>
              </p>
            )}
          </>
        ) : parish.canAcceptDonations ? (
          <p className="text-charcoal-muted">
            {parish.name} isn't accepting donations right now.
          </p>
        ) : (
          <p className="text-charcoal-muted">
            This parish isn't accepting online donations yet.
          </p>
        )}
      </div>

      <div className="bg-white border border-surface-border rounded p-6">
        <h3 className="text-xl font-semibold font-display text-charcoal mb-4">Contact Information</h3>
        <div className="space-y-4">
          {address && (
            <div className="flex items-start gap-3">
              <MapPin className="w-5 h-5 text-charcoal-muted mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-charcoal/90">{address}</p>
                {googleMapsUrl && (
                  <a
                    href={googleMapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-charcoal-muted hover:text-charcoal mt-1 inline-flex items-center gap-1"
                  >
                    View on Google Maps
                    <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            </div>
          )}
          {parish.phoneNumber && (
            <div className="flex items-center gap-3">
              <Phone className="w-5 h-5 text-charcoal-muted flex-shrink-0" />
              <a
                href={`tel:${parish.phoneNumber}`}
                className="text-charcoal/90 hover:text-charcoal"
              >
                {parish.phoneNumber}
              </a>
            </div>
          )}
          {parish.email && (
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-charcoal-muted flex-shrink-0" />
              <a
                href={`mailto:${parish.email}`}
                className="text-charcoal/90 hover:text-charcoal break-all"
              >
                {parish.email}
              </a>
            </div>
          )}
        </div>
      </div>

      {hasMassTimes && (
        <div className="bg-white border border-surface-border rounded p-6">
          <div className="flex items-center gap-2 mb-4">
            <Clock className="w-5 h-5 text-charcoal-muted" />
            <h3 className="text-xl font-semibold font-display text-charcoal">Mass Times</h3>
          </div>
          <div className="space-y-1">
            {massTimesContent}
          </div>
        </div>
      )}

      {hasConfessionTimes && (
        <div className="bg-white border border-surface-border rounded p-6">
          <div className="flex items-center gap-2 mb-4">
            <Clock className="w-5 h-5 text-charcoal-muted" />
            <h3 className="text-xl font-semibold font-display text-charcoal">Confession Times</h3>
          </div>
          <div className="space-y-1">
            {confessionTimesContent}
          </div>
        </div>
      )}

      {hasStaff && (
        <div className="bg-white border border-surface-border rounded p-6">
          <div className="flex items-center gap-2 mb-4">
            <Users className="w-5 h-5 text-charcoal-muted" />
            <h3 className="text-xl font-semibold font-display text-charcoal">Parish Staff</h3>
          </div>
          <div className="space-y-4">
            {staffData.map((member, index) => (
              <div key={index} className="flex flex-col sm:flex-row sm:items-center sm:justify-between py-2 border-b border-surface-border last:border-b-0">
                <div>
                  <span className="text-sm text-charcoal-muted">{member.title}</span>
                  <p className="text-base font-semibold text-charcoal">{member.name}</p>
                </div>
                <div className="flex items-center gap-4 mt-1 sm:mt-0">
                  {member.email && (
                    <a href={`mailto:${member.email}`} className="flex items-center gap-1 text-sm text-charcoal-muted hover:text-charcoal">
                      <Mail className="w-3.5 h-3.5" />
                      {member.email}
                    </a>
                  )}
                  {member.phone && (
                    <a href={`tel:${member.phone}`} className="flex items-center gap-1 text-sm text-charcoal-muted hover:text-charcoal">
                      <Phone className="w-3.5 h-3.5" />
                      {member.phone}
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {events.length > 0 && (
        <div className="bg-white border border-surface-border rounded p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-gold" />
              <h3 className="text-xl font-semibold font-display text-charcoal">Upcoming Events</h3>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {events.map((event) => (
              <EventCard
                key={event.id}
                event={event}
                onClick={() => navigate(`/parishes/${id}/events/${event.id}`)}
              />
            ))}
          </div>
        </div>
      )}

    </div>
  )
}
